import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { GoSidebarExpand } from "react-icons/go";
import React from "react";

import Header from "./Header";
import SideMenu from "./SideMenu";
import MainContent from "./MainContent";
import Footer from "./Footer";
import SideModal from "./SideModal";
import { CategoryApiResponse, fetchCategory } from "../../api/categoryApi";
import { SubCategoryApiResponse, fetchSubCategory } from "../../api/subCategoryApi";
import { YoutubeContentApiResponse, fetchYoutubeContent } from "../../api/youtubeContentApi";
import { getScreenWidth } from "../../constants";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const StyledHeader = styled.header`
  width: 100%;
  height: 70px;
  background-color: #ffc736;
  box-sizing: border-box;
  position: fixed;
  z-index: 999;
`;

const Block = styled.div`
  display: flex;
  flex: 1;
`;

const StyledMainContent = styled.nav`
  flex: 1;
  box-sizing: border-box;
  margin-top: 70px;
`;

const StyledSideMenu = styled.nav`
  top: 70px;
  left: 0;
  padding: 5px 0 0 5px;
  height: 100%;
  position: fixed;
  background-color: #f2efe7;
  box-sizing: border-box;
`;

const StyledFooter = styled.footer`
  width: 100%;
  background-color: #f0f0f0;
  padding: 10px;
  box-sizing: border-box;
  text-align: center;
`;

const StyledModal = styled(Modal)`
  .modal-content {
    width: 200px;
  }
  .modal-body {
    padding: 5px 0 0 5px;
    background-color: #f2efe7;
  }
`;

const SideModalCloseButton = styled(GoSidebarExpand)`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  border: none;
  position: fixed;
  left: 200px;
  background-color: #f2efe7;
  padding: 5px;

  &:hover {
    border: 0px solid #ddd;
    border-radius: 5px;
    background-color: #c0bdb5;
  }
`;

const SideMenuWidth = "200px";
const SideMenuWidthMin = "80px";

const Contents = () => {
  const { isMobile, isDesktop } = getScreenWidth();

  const [youtubeContents, setYoutubeContents] = useState<YoutubeContentApiResponse[]>([]);
  const [categories, setCategories] = useState<CategoryApiResponse[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryApiResponse[]>([]);
  const [isSideMenuExpanded, setIsSideMenuExpanded] = useState(true);
  const [isShowSideModal, setIsShowSideModal] = useState(false);
  const [isPageScrolling, setIsPageScrolling] = useState(false);
  const [activeCategory, setActiveCategory] = useState<Record<string, boolean>>({});

  const refs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  categories.forEach((category) => {
    if (!refs.current[category.id]) {
      refs.current[category.id] = React.createRef<HTMLDivElement>();
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contentsData = await fetchYoutubeContent();
        setYoutubeContents(contentsData);

        const categoryData = await fetchCategory();
        setCategories(categoryData);

        const subCategoryData = await fetchSubCategory();
        setSubCategories(subCategoryData);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();

    if (isDesktop) {
      setIsSideMenuExpanded(true);
      setIsShowSideModal(false);
    } else {
      setIsSideMenuExpanded(false);
    }

    // NOTE: ページスクロールを検知後、すぐに戻す
    if (isPageScrolling) {
      setIsPageScrolling(false);
    }
  }, [isDesktop, isPageScrolling]);

  useEffect(() => {
    // NOTE: サイドメニューをコンテンツの表示に合わせてハイライトする
    const observerOptions = {
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        setActiveCategory((prev) => ({
          ...prev,
          [id]: entry.isIntersecting,
        }));
      });
    }, observerOptions);

    categories.forEach((category) => {
      if (!refs.current[category.id]) {
        refs.current[category.id] = React.createRef<HTMLDivElement>();
      }
      const currentRef = refs.current[category.id];
      if (currentRef && currentRef.current) {
        observer.observe(currentRef.current);
      }
    });

    return () => {
      categories.forEach((category) => {
        const currentRef = refs.current[category.id];
        if (currentRef && currentRef.current) {
          observer.unobserve(currentRef.current);
        }
      });
    };
  }, [categories]);

  const toggleSideMenu = () => {
    if (isDesktop) {
      setIsSideMenuExpanded(!isSideMenuExpanded);
    } else {
      setIsShowSideModal(!isShowSideModal);
    }
  };

  const closeModal = () => {
    setIsPageScrolling(true);
    setIsShowSideModal(false);
  };

  return (
    <>
      <Container>
        <StyledHeader>
          <Header onMenuClick={toggleSideMenu} />
        </StyledHeader>
        <Block>
          {!isMobile && (
            <StyledSideMenu
              style={{
                width: isSideMenuExpanded ? SideMenuWidth : SideMenuWidthMin,
              }}
            >
              <SideMenu
                categories={categories}
                subCategories={subCategories}
                isExpanded={isSideMenuExpanded}
                activeCategory={activeCategory}
                onNavClick={() => setIsPageScrolling(true)}
              />
            </StyledSideMenu>
          )}
          <StyledMainContent
            style={{
              marginLeft: isMobile ? 0 : isSideMenuExpanded ? SideMenuWidth : SideMenuWidthMin,
            }}
          >
            <MainContent
              youtubeContents={youtubeContents}
              categories={categories}
              subCategories={subCategories}
              isScrolling={isPageScrolling}
              refs={refs.current}
            />
          </StyledMainContent>
        </Block>
        <StyledFooter>
          <Footer />
        </StyledFooter>
      </Container>
      {isShowSideModal && (
        <>
          <StyledModal
            show={isShowSideModal}
            onHide={() => setIsShowSideModal(false)}
            fullscreen={true}
          >
            {/* <SideModalCloseButton > */}
            <SideModalCloseButton onClick={() => setIsShowSideModal(false)} />
            {/* </SideModalCloseButton> */}
            <SideModal
              categories={categories}
              subCategories={subCategories}
              activeCategory={activeCategory}
              closeModal={closeModal}
            />
          </StyledModal>
        </>
      )}
    </>
  );
};

export default Contents;
